import React, { useEffect, useRef, useState } from "react";
import QRCode from 'qrcode';
import { useTerminalConfig } from "../../Path/NewTerminalConfigs";
import { createQRCodeGenerator, getAllQRCodes, uploadQRImage } from "../../Services/terminal";
import { FaTimes } from "react-icons/fa";
import { base64ToFile } from "./TerminalForm";
import { uniq } from "lodash-es";
import { validateURL } from "../util/linkValidation";
import { useTranslation } from "react-i18next";

const QRCodeGenerator = () => {
    const { terminalConfig, setTerminalConfig } = useTerminalConfig();
    const [links, setLinks] = useState();
    const [newLink, setNewLink] = useState("");
    const [image, setImage] = useState(null);
    const [bgQrImage, setBgQrImage] = useState(null);
    const [title, setTitle] = useState("");
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const fileInputRef = useRef(null);
    const fileInputRefBg = useRef(null);
    const [isQrLinkValid, setIsQrLinkValid] = useState(true);
    const [loader, setLoader] = useState(false);
    const { t } = useTranslation();
    const handleButtonClick = () => {
        fileInputRef.current.click();
    };
    const handleButtonClickBg = () => {
        fileInputRefBg.current.click();
    };
    const handleAddLink = async () => {
        if (title && (newLink || image) && !loader) {
            try {
                setLoader(true);
                const formData = new FormData();
                const res = await createQRCodeGenerator({ title, terminalId: terminalConfig.terminalId });
                if (bgQrImage) {
                    formData.append("backgroundImage", base64ToFile(bgQrImage))
                }
                if (newLink) {
                    const dataUrl = await QRCode.toDataURL(newLink);
                    const file = base64ToFile(dataUrl, 'qr-code.png');
                    formData.append("image", file)
                } if (!newLink) {
                    formData.append("image", base64ToFile(image))
                }
                const resImageUpload = await uploadQRImage(res.data?.id, formData);
                if (res.data.status === 'success' && resImageUpload.data.status === 'success') {
                    getAllQRCodes(terminalConfig.terminalId).then(res => setLinks(res.data?.data))
                    setSuccessMessage(t("qr_success"));
                }
            } catch (e) {
                console.error(e, 33)
                setErrorMessage(t("qr_error"));
            } finally {
                setNewLink("");
                setTitle("");
                setBgQrImage(null);
                setImage(null);
                setTimeout(() => {
                    setErrorMessage('');
                    setSuccessMessage('');
                    setLoader(false);
                }, 2000)
            }
        }
    };

    const handleLinkValidation = (value) => {
        setNewLink(value);
        if (!value || validateURL(value)) {
            setIsQrLinkValid(true);
        } else {
            setIsQrLinkValid(false);
        }
    };

    const handleFileChange = (event) => {
        handleLinkValidation(newLink);
        const file = event.target.files?.[0];
        const maxSize = 25 * 1024 * 1024; // 25MB in bytes
        if (file) {
            if (file.size > maxSize) {
                setErrorMessage(t("file_size_error",{ size: maxSize / 1024 / 1024 }));
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                setImage(reader.result);
            };
            reader.readAsDataURL(file);
            setErrorMessage('');
        }
    };

    const handleFileChangeBg = (event) => {
        const file = event.target.files?.[0];
        const maxSize = 25 * 1024 * 1024; // 25MB in bytes
        if (file) {
            if (file.size > maxSize) {
                setErrorMessage(t("file_size_error", { size: maxSize / 1024 / 1024 }));
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                setBgQrImage(reader.result);
            };
            reader.readAsDataURL(file);
            setErrorMessage('');
        }
    };

    const clearImage = () => {
        setImage(null);
        fileInputRef.current.value = null;
    };
    const clearImageBg = () => {
        setBgQrImage(null);
        fileInputRefBg.current.value = null;
    };
    useEffect(() => {
        if (terminalConfig?.terminalId) {
            getAllQRCodes(terminalConfig.terminalId).then(res => setLinks(res.data?.data))
        }
    }, [terminalConfig?.terminalId]);

    return (
        <div className="mt-6">
            <h2 className="text-xl font-semibold mb-2 text-white">{t("qr_code_generator")}</h2>

            <div className="flex flex-col space-y-4 mb-4">
                <div className="flex space-x-2">
                    <input type="text" className="flex-1 px-4 py-2 border border-gray-300 rounded max-w-sm" placeholder={`${t("title")}*`} value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                {bgQrImage ? <div className="relative bg-gray-100 rounded w-32 h-32 overflow-hidden">
                    <FaTimes
                        className="absolute top-1 right-1 text-red-600 bg-white rounded-full p-1 cursor-pointer"
                        onClick={clearImageBg}
                        size={18}
                    />
                    <img
                        src={bgQrImage}
                        alt="Preview"
                        className="object-contain w-full h-full"
                    />
                </div> :
                    <button className="bg-blue-600 text-white px-4 py-2 rounded-button w-fit rounded" onClick={handleButtonClickBg}>
                        {t("upload_qrbg")}
                    </button>
                }

                <p className="text-md font-medium text-white">{t("qr_upoad_info")}</p>
                <div className="bg-white border-2 rounded p-4">


                    <div className="flex space-x-2">
                        <input
                            type="url"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded max-w-sm"
                            placeholder={`${t("enter_qrlink")}*`}
                            disabled={!!image}
                            value={newLink}
                            onChange={(e) => handleLinkValidation(e.target.value)}
                        />
                    </div>
                    {!isQrLinkValid && (
                        <p className="text-red-500 text-sm mt-1">{t("validurl")}</p>
                    )}

                    <div className="flex w-full text-white">
                        <input
                            type="file"
                            className="hidden"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            accept="image/png,image/jpeg,image/svg+xml"
                        />
                    </div>
                    <div className="flex flex-row gap-2 my-4">
                        <div className="h-1 w-[200px] bg-black self-center"></div>
                        <span className="text-black">{t("or_message")}</span>
                        <div className="h-1 w-[200px] bg-black self-center"></div>
                    </div>
                    {image ? <div className="relative bg-gray-100 rounded w-32 h-32 overflow-hidden">
                        <FaTimes
                            className="absolute top-1 right-1 text-red-600 bg-white rounded-full p-1 cursor-pointer"
                            onClick={clearImage}
                            size={18}
                        />
                        <img
                            src={image}
                            alt="Preview"
                            className="object-contain w-full h-full"
                        />
                    </div> :
                        <button disabled={!!newLink} className="bg-blue-600 text-white px-4 py-2 rounded-button w-fit rounded disabled:bg-gray-100 disabled:text-gray-400 disabled:font-black" onClick={handleButtonClick}>
                            {t("upload_qr_code")}
                        </button>
                    }
                </div>
                <div className="flex w-full text-white">
                    <input
                        type="file"
                        className="hidden"
                        onChange={handleFileChangeBg}
                        ref={fileInputRefBg}
                        accept="image/png,image/jpeg,image/svg+xml"
                    />
                </div>
                {successMessage && <span className="text-green-600 text-bold">{successMessage}</span>}
                {errorMessage && <span className="text-red-600 text-bold">{errorMessage}</span>}
                <button
                    onClick={handleAddLink}
                    disabled={!(title && (newLink || image))}
                    className="px-4 w-fit py-2 bg-blue-600 text-white rounded hover:bg-indigo-600 disabled:bg-gray-100 disabled:text-gray-400 disabled:font-black"
                >
                    {loader ? (
                        <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
                    ) : (
                        t("save_qr_code")
                    )}
                </button>
            </div>

            <h6 className="text-xl font-semibold mb-2 text-white w-full">{t("featuredQrCodes")}</h6>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-2">
                {links?.map((item) => (
                    <div key={item.id} className="border rounded bg-gray-50 h-64 cursor-pointer relative"
                        onClick={() => {
                            if (terminalConfig.qrIds.includes(item.id)) {
                                setTerminalConfig((prev) => ({ ...prev, qrIds: terminalConfig.qrIds.filter((id) => id !== item.id) }))
                            } else {
                                setTerminalConfig((prev) => ({ ...prev, qrIds: terminalConfig.qrIds?.length > 0 ? uniq([...terminalConfig.qrIds, item.id]) : [item.id] }))
                            }
                        }}
                        style={{
                            border: terminalConfig?.qrIds?.includes(item.id) ? '5px solid blue' : '',
                            backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '200px', backgroundImage: `url(${item?.backgroundImageUrl})`
                        }}
                    >
                        <div className="absolute inset-0 bg-black/40 rounded-lg z-0" />
                        <div className="flex justify-between relative z-10">
                            {/* <span className="text-lg w-3/5 h-10 break-words font-bold p-2 text-white bg-black/20">{item.title}</span> */}
                            <h3 className="text-center text-black bg-white/70 p-2 text-xl 3xl:text-[4rem] font-semibold w-fit mt-4 min-w-[50%] rounded -[5px]">
                                {item.title}
                            </h3>
                        </div>
                        <div className={`flex flex-col h-44  justify-end items-end relative z-10 ${terminalConfig?.qrIds?.includes(item.id) ? 'mt-2' : 'mt-4'} `}>
                            <div className="rounded-lg p-1 shadow-md max-w-full max-h-full bg-white">
                                <img
                                    src={item.imageUrl}
                                    alt="preview"
                                    className="w-[100px] h-[100px] object-contain"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default QRCodeGenerator;
